import { useSetHeaderMobileInformation } from '@ingenico/react-section-utils';
import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_RESOURCES } from '@ingenico/util-i18n-config';
import { CoreButton } from '@ingenico/react-ui/core-button';
import AddIcon from '@mui/icons-material/Add';
import { useMediaQuery } from '@mui/material';
import { useSetAtom } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useResourcesListVM } from '../../components/resources-list/useResourcesList.vm';
import { openCreateResourceModalAtom } from '../../components/create-resource-modal/CreateResourceModal.atom';
import { useDeviceManagementCapability } from '../../hooks';

interface UseResourcesPageVMOutput {
  readonly isMobile: boolean;
  readonly canCreate: boolean;
  readonly createButton: ReactElement;
  readonly resourcesListVM: ReturnType<typeof useResourcesListVM>;
  readonly handleResourceCreated: () => void;
  readonly pageTitle: string;
  readonly pageSubtitle: string;
}

export function useResourcesPageVM(): UseResourcesPageVMOutput {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_RESOURCES));
  const setCreateResourceModalOpen = useSetAtom(openCreateResourceModalAtom);
  const canCreate = useDeviceManagementCapability('resources.create');
  const isMobile = useMediaQuery('(max-width: 766px)');
  const setHeaderMobileInformation = useSetHeaderMobileInformation();

  const pageTitle = t('navigation.page.resources', { ns: ALL_LNGS_NAMESPACES.COMMON });
  const pageSubtitle = t('navigation.subtitle.resources', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT });

  const openCreateModal = useCallback(() => {
    setCreateResourceModalOpen(true);
  }, [setCreateResourceModalOpen]);

  const resourcesListVM = useResourcesListVM({
    canCreate,
    onCreateResource: openCreateModal,
  });

  const handleResourceCreated = useCallback(() => {
    resourcesListVM.response.refetch?.();
  }, [resourcesListVM.response]);

  const createButton = useMemo(
    () => (
      <CoreButton variant="contained" startIcon={<AddIcon />} onClick={openCreateModal}>
        {t('button.create', { ns: ALL_LNGS_NAMESPACES.COMMON })}
      </CoreButton>
    ),
    [openCreateModal, t],
  );

  useEffect(() => {
    setHeaderMobileInformation({
      title: pageTitle,
      subtitle: pageSubtitle,
      kebabOptions: canCreate
        ? [
            {
              icon: <AddIcon />,
              label: t('button.create', { ns: ALL_LNGS_NAMESPACES.COMMON }),
              onClick: openCreateModal,
              key: 'add-resource',
            },
          ]
        : [],
    });
  }, [canCreate, openCreateModal, pageSubtitle, pageTitle, setHeaderMobileInformation, t]);

  return {
    isMobile,
    canCreate,
    createButton,
    resourcesListVM,
    handleResourceCreated,
    pageTitle,
    pageSubtitle,
  };
}
